/**
 * Canonical EIP-712 typed-data definitions for this repo's contracts —
 * `DealEscrow.sol` and, at the bottom of this file, `AdminRegistry.sol`. The
 * two use SEPARATE EIP-712 domains; see the note above PROMOTE_TYPES.
 *
 * These strings and field lists must match the contract's typehash constants
 * exactly (`ACTIVATE_TYPEHASH`, `RELEASE_TYPEHASH`, `DISPUTE_TYPEHASH`,
 * `REFUND_TYPEHASH`, `DESTROY_TYPEHASH` in `contracts/DealEscrow.sol`). A
 * mismatch does NOT throw: `signTypedData` happily signs whatever shape it is
 * given, and `ecrecover` happily recovers *an* address from it — just not the
 * signer's address. The failure is silent until someone notices a signature
 * that should have been valid was rejected, or worse, that a different
 * address than the intended signer was accepted.
 *
 * This file is the canonical copy for the demo and the seed script —
 * `scripts/seed-demo.ts`, `demo/src/flows/runDeal.ts` and
 * `demo/src/flows/dispute.ts` import it rather than each declaring their own
 * shape, which is what used to let one of them drift from the contract
 * silently. Several `test/*.ts` files still declare their own literals
 * (`Release:`, `Activate:`, and so on) rather than importing from here — for
 * the tests, the contract's typehash constants remain the oracle, checked
 * directly against a live deployment rather than against this file's say-so.
 */

export const ESCROW_DOMAIN_NAME = "DealEscrow";
export const ESCROW_DOMAIN_VERSION = "1";

export const ACTIVATE_TYPES = { Activate: [
  { name: "dealId", type: "bytes32" }, { name: "nonce", type: "uint256" },
]};

export const RELEASE_TYPES = { Release: [
  { name: "dealId", type: "bytes32" }, { name: "to", type: "address" },
  { name: "amount", type: "uint256" }, { name: "nonce", type: "uint256" },
]};

export const DISPUTE_TYPES = { Dispute: [
  { name: "dealId", type: "bytes32" }, { name: "nonce", type: "uint256" },
]};

export const REFUND_TYPES = { Refund: [
  { name: "dealId", type: "bytes32" }, { name: "nonce", type: "uint256" },
]};

export const DESTROY_TYPES = { DestroyShare: [
  { name: "dealId", type: "bytes32" }, { name: "nonce", type: "uint256" },
]};

/**
 * Party rotation (spec §4.2, wallet recovery).
 *
 * `commitmentsHash` is `keccak256(abi.encode(bytes32[3]))` — a single bytes32,
 * not the array itself. A fixed-size array is expressible in EIP-712, but it is
 * a place where this file's encoding and the contract's `abi.encode` of the
 * same struct member can drift apart without either side noticing; a bytes32 is
 * trivially the same 32 bytes on both sides. What it binds is *which* re-split
 * the counterparty is acknowledging, not merely that a rotation happens.
 */
export const ROTATE_TYPES = { RotateParty: [
  { name: "dealId", type: "bytes32" }, { name: "oldParty", type: "address" },
  { name: "newParty", type: "address" }, { name: "commitmentsHash", type: "bytes32" },
  { name: "nonce", type: "uint256" },
]};

export const CANCEL_ROTATION_TYPES = { CancelRotation: [
  { name: "dealId", type: "bytes32" }, { name: "nonce", type: "uint256" },
]};

/**
 * Admin registry (spec §4.2.1). These belong to `contracts/AdminRegistry.sol`,
 * NOT to the escrow, and they are signed under a different EIP-712 domain —
 * `name: "AdminRegistry", version: "1"` against the registry's own address,
 * where everything above uses `DealEscrow`/`1` against the escrow's.
 *
 * That separation is the point and not an accident of naming. The domain
 * separator is part of what a signer signs, so a signature produced for one
 * contract cannot be replayed against the other even where the struct shapes
 * would otherwise line up. Anything signing these must build its own domain;
 * reusing ESCROW_DOMAIN_NAME here would silently produce signatures the
 * registry rejects — and, worse, would make escrow authorisations and registry
 * authorisations interchangeable if the shapes ever converged.
 *
 * The field lists must match `PROMOTE_TYPEHASH` and `DEMOTE_TYPEHASH` in
 * AdminRegistry.sol character for character, for the reason given at the top
 * of this file: a mismatch is silent.
 */
export const ADMIN_REGISTRY_DOMAIN_NAME = "AdminRegistry";
export const ADMIN_REGISTRY_DOMAIN_VERSION = "1";

export const PROMOTE_TYPES = { PromoteAdmin: [
  { name: "who", type: "address" }, { name: "nonce", type: "uint256" },
]};

export const DEMOTE_TYPES = { DemoteAdmin: [
  { name: "who", type: "address" }, { name: "nonce", type: "uint256" },
]};
